import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { formatMAD, type Locale } from "@tarawa/shared";
import { fr, type Dictionary } from "../locales/fr";
import { ar } from "../locales/ar";
import { ApiError, NetworkError } from "./api";

type Leaves<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];
export type TKey = Leaves<Dictionary>;
type Vars = Record<string, string | number>;

const DICTS: Record<Locale, Dictionary> = { fr, ar };
const STORAGE_KEY = "tw_locale";

function lookup(dict: Dictionary, key: string): string | undefined {
  let node: unknown = dict;
  for (const part of key.split(".")) {
    if (node && typeof node === "object" && part in node) node = (node as Record<string, unknown>)[part];
    else return undefined;
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(text: string, vars?: Vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "fr" || saved === "ar") return saved;
  } catch {
    /* storage unavailable */
  }
  return navigator.language?.toLowerCase().startsWith("ar") ? "ar" : "fr";
}

interface I18n {
  locale: Locale;
  dir: "ltr" | "rtl";
  setLocale: (l: Locale) => void;
  /** Typed translation lookup with {var} interpolation. */
  t: (key: TKey, vars?: Vars) => string;
  /** Dynamic key lookup (e.g. error codes); returns undefined when missing. */
  tx: (key: string, vars?: Vars) => string | undefined;
  money: (cents: number) => string;
  date: (iso: string | Date, withTime?: boolean) => string;
  /** Picks the Arabic variant of a bilingual DB field when available. */
  ln: (fr: string | null | undefined, ar: string | null | undefined) => string;
  /** Human message for any error thrown by the API client. */
  errorMessage: (err: unknown) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const dir = locale === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    document.title = locale === "ar" ? "طراوة — توصيل المشتريات" : "Tarawa — Livraison de courses";
  }, [locale, dir]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<I18n>(() => {
    const dict = DICTS[locale];
    const tx = (key: string, vars?: Vars) => {
      const s = lookup(dict, key) ?? lookup(fr, key);
      return s === undefined ? undefined : interpolate(s, vars);
    };
    const money = (cents: number) => formatMAD(cents, locale);
    return {
      locale,
      dir,
      setLocale,
      t: (key, vars) => tx(key, vars) ?? key,
      tx,
      money,
      date: (iso, withTime = true) =>
        new Intl.DateTimeFormat(locale === "ar" ? "ar-MA" : "fr-MA", {
          day: "numeric",
          month: "short",
          year: "numeric",
          ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
        }).format(new Date(iso)),
      ln: (frText, arText) => (locale === "ar" && arText ? arText : (frText ?? "")),
      errorMessage: (err) => {
        if (err instanceof NetworkError) return tx("errors.network")!;
        if (err instanceof ApiError) {
          const vars: Vars = {};
          if (typeof err.details.minOrderCents === "number") vars.amount = money(err.details.minOrderCents);
          if (typeof err.details.opensAt === "string") vars.start = err.details.opensAt;
          if (typeof err.details.closesAt === "string") vars.end = err.details.closesAt;
          return tx(`errors.${err.code}`, vars) ?? tx("errors.generic")!;
        }
        return tx("errors.generic")!;
      },
    };
  }, [locale, dir, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

/** Field-level message for a VALIDATION_ERROR response. */
export function fieldError(i18n: I18n, err: unknown, field: string): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const code = err.fields[field];
  return code ? (i18n.tx(`errors.${code}`) ?? i18n.tx("errors.INVALID")) : undefined;
}
